import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyCustomerProducts } from 'src/components/customer-product/grid/EmptyCustomerProducts'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  CUSTOMER_PRODUCTS_GRID_COLUMNS_STORAGE_KEY,
  customerProductPageSizeOptions,
} from 'src/constants/customer-product/constants'
import { buildCustomerProductColumns } from 'src/constants/customer-product/customerProductGrid'
import { CustomerProductsTableFields } from 'src/constants/customer-product/enums'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { getCustomerProducts, getCustomerProductsLoading } from 'src/selectors/customerProduct'
import { filterCustomerProducts } from 'src/utils/customerProductList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canManage: boolean
  onEditProduct(product: CustomerProduct): void
  onDeactivateProduct(product: CustomerProduct): void
}

const getRowId = (row: CustomerProduct) => {
  return row.id
}

// The product identifies the row and the actions column has no meaning when hidden; both also
// carry `hideable: false`, which is what removes them from the manage-columns panel. This
// guard additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  CustomerProductsTableFields.product,
  CustomerProductsTableFields.actions,
]

const CustomerProductsTableComponent = ({
  searchQuery,
  canManage,
  onEditProduct,
  onDeactivateProduct,
}: Props) => {
  const products = useSelector(getCustomerProducts)
  const isLoading = useSelector(getCustomerProductsLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(customerProductPageSizeOptions)

  // A fresh array each render would reset the grid's column state, so the factory is memoized.
  const productColumns = useMemo(() => {
    return buildCustomerProductColumns(onEditProduct, onDeactivateProduct, canManage)
  }, [onEditProduct, onDeactivateProduct, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    productColumns,
    CUSTOMER_PRODUCTS_GRID_COLUMNS_STORAGE_KEY,
  )

  // The merged list is complete (the endpoint takes no search param), so filtering is here.
  const visibleProducts = useMemo(() => {
    return filterCustomerProducts(products, searchQuery)
  }, [products, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return productColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [productColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      productColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [productColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyCustomerProducts = () => {
      return <EmptyCustomerProducts hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyCustomerProducts
  }, [hasSearchQuery])

  // Pinning is intentionally left uncontrolled, for the reason documented on CustomersTable:
  // `useGridColumns`'s persisted list has no left/right distinction.
  return (
    <GridTable<CustomerProduct>
      rows={visibleProducts}
      columns={productColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={customerProductPageSizeOptions}
      initialState={{
        sorting: { sortModel: [{ field: CustomerProductsTableFields.product, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="customer-products-table"
      rowDataTestIdPrefix="customer-products"
    />
  )
}

export const CustomerProductsTable = memo(CustomerProductsTableComponent)
